import { env } from '@/app/config/env.config';
import type { ApiEnvelope, ApiMeta } from '@/shared/types/api';
import { ApiError, ApiNetworkError } from './api-error';
import { isApiFailure, isApiSuccess } from './api-response';

/** A list response carrying both the page of rows and its pagination meta. */
export interface ApiListResult<T> {
  data: T[];
  meta: ApiMeta;
}

/**
 * Generate a short request id (UUID v4 if available, else timestamp+random).
 * Used as `X-Request-Id` on every request for log correlation.
 */
function generateRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface RequestOptions {
  method?: HttpMethod;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  skipAuth?: boolean;
  skipRefresh?: boolean;
}

interface InternalConfig {
  baseUrl: string;
  fetchImpl: typeof fetch;
  getAccessToken?: (() => string | null) | undefined;
  refreshAccessToken?: (() => Promise<string | null>) | undefined;
  onUnauthorized?: (() => void) | undefined;
}

export interface ApiAuthHandlers {
  getAccessToken: () => string | null;
  refreshAccessToken: () => Promise<string | null>;
  onUnauthorized: () => void;
}

let defaultAuthHandlers: ApiAuthHandlers | null = null;

/**
 * Connects the shared HTTP client to the auth feature. This is the single
 * reuse point future feature services should rely on for authenticated API
 * calls; feature modules should not hand-roll fetch wrappers.
 */
export function configureApiAuth(handlers: ApiAuthHandlers | null): void {
  defaultAuthHandlers = handlers;
}

/**
 * Current access token, for non-fetch consumers that still need auth — e.g. the
 * Socket.IO handshake for live notifications. Returns null when signed out.
 */
export function getCurrentAccessToken(): string | null {
  return defaultAuthHandlers?.getAccessToken() ?? null;
}

async function execute<T>(
  path: string,
  options: RequestOptions = {},
  config: InternalConfig,
): Promise<{ data: T; meta?: ApiMeta }> {
  const url = path.startsWith('http') ? path : `${config.baseUrl}${path}`;
  const requestId = generateRequestId();
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Request-Id': requestId,
    ...(options.headers ?? {}),
  };
  const accessToken = options.skipAuth
    ? null
    : (config.getAccessToken?.() ?? defaultAuthHandlers?.getAccessToken());
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  if (options.body !== undefined && !(options.body instanceof FormData)) {
    headers['Content-Type'] = headers['Content-Type'] ?? 'application/json';
  }

  const init: RequestInit = {
    method,
    headers,
    credentials: 'include',
    ...(options.signal !== undefined ? { signal: options.signal } : {}),
  };
  if (options.body !== undefined) {
    init.body =
      options.body instanceof FormData ? options.body : JSON.stringify(options.body);
  }

  let response: Response;
  try {
    response = await config.fetchImpl(url, init);
  } catch (cause) {
    throw new ApiNetworkError(
      `Network error contacting ${url}: ${cause instanceof Error ? cause.message : String(cause)}`,
      cause,
    );
  }

  // 204 / empty body
  if (response.status === 204) {
    return { data: undefined as T };
  }

  let envelope: ApiEnvelope<T>;
  try {
    envelope = (await response.json()) as ApiEnvelope<T>;
  } catch (cause) {
    throw new ApiNetworkError(
      `Failed to parse JSON response from ${url}`,
      cause,
    );
  }

  if (isApiFailure(envelope)) {
    throw new ApiError({
      errorCode: envelope.errorCode,
      message: envelope.message,
      details: envelope.details,
    }, {
      status: envelope.statusCode || response.status,
      requestId: envelope.requestId || requestId,
    });
  }

  if (!isApiSuccess(envelope)) {
    throw new ApiNetworkError(`Invalid API envelope received from ${url}`);
  }

  return {
    data: envelope.data,
    ...(envelope.meta ? { meta: envelope.meta } : {}),
  };
}

async function request<T>(
  path: string,
  options: RequestOptions = {},
  config: InternalConfig,
): Promise<{ data: T; meta?: ApiMeta }> {
  try {
    return await execute<T>(path, options, config);
  } catch (error) {
    const shouldRefresh =
      error instanceof ApiError &&
      error.status === 401 &&
      !options.skipAuth &&
      !options.skipRefresh &&
      (config.refreshAccessToken !== undefined ||
        defaultAuthHandlers?.refreshAccessToken !== undefined);

    if (!shouldRefresh) {
      throw error;
    }

    const nextAccessToken = await (
      config.refreshAccessToken ?? defaultAuthHandlers?.refreshAccessToken
    )?.();
    if (!nextAccessToken) {
      (config.onUnauthorized ?? defaultAuthHandlers?.onUnauthorized)?.();
      throw error;
    }

    try {
      return await execute<T>(
        path,
        { ...options, headers: { ...(options.headers ?? {}) }, skipRefresh: true },
        { ...config, getAccessToken: () => nextAccessToken },
      );
    } catch (retryError) {
      if (retryError instanceof ApiError && retryError.status === 401) {
        (config.onUnauthorized ?? defaultAuthHandlers?.onUnauthorized)?.();
      }
      throw retryError;
    }
  }
}

/**
 * Public API client. Thin wrapper over fetch; envelope handling lives here so
 * feature services stay declarative.
 */
export function createApiClient(
  overrides: Partial<InternalConfig> = {},
): {
    get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) => Promise<T>;
    list: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) => Promise<ApiListResult<T>>;
    post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) => Promise<T>;
    patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) => Promise<T>;
    put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) => Promise<T>;
    delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) => Promise<T>;
  } {
  const config: InternalConfig = {
    baseUrl: overrides.baseUrl ?? env.apiBaseUrl,
    fetchImpl: overrides.fetchImpl ?? fetch.bind(globalThis),
    getAccessToken: overrides.getAccessToken ?? defaultAuthHandlers?.getAccessToken,
    refreshAccessToken:
      overrides.refreshAccessToken ?? defaultAuthHandlers?.refreshAccessToken,
    onUnauthorized: overrides.onUnauthorized ?? defaultAuthHandlers?.onUnauthorized,
  };

  return {
    get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
      request<T>(path, { ...options, method: 'GET' }, config).then((r) => r.data),
    list: <T>(
      path: string,
      options?: Omit<RequestOptions, 'method' | 'body'>,
    ): Promise<ApiListResult<T>> =>
      request<T[]>(path, { ...options, method: 'GET' }, config).then((r) => ({
        data: r.data ?? [],
        meta: r.meta ?? {},
      })),
    post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
      request<T>(path, { ...options, method: 'POST', body }, config).then((r) => r.data),
    patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
      request<T>(path, { ...options, method: 'PATCH', body }, config).then((r) => r.data),
    put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
      request<T>(path, { ...options, method: 'PUT', body }, config).then((r) => r.data),
    delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
      request<T>(path, { ...options, method: 'DELETE' }, config).then((r) => r.data),
  };
}

/**
 * Authenticated raw fetch for non-JSON responses (e.g. streamed images served
 * with `@RawResponse()`). Applies the base URL, bearer token, and a request
 * id, and retries once after a token refresh on 401. Returns the raw Response
 * — the caller reads `.blob()`/`.arrayBuffer()` itself (no envelope parsing).
 */
export async function authorizedFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const url = path.startsWith('http') ? path : `${env.apiBaseUrl}${path}`;
  const run = (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = {
      'X-Request-Id': generateRequestId(),
      ...((init.headers as Record<string, string> | undefined) ?? {}),
    };
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(url, { ...init, headers, credentials: 'include' });
  };

  let response = await run(defaultAuthHandlers?.getAccessToken() ?? null);
  if (response.status === 401 && defaultAuthHandlers?.refreshAccessToken) {
    const next = await defaultAuthHandlers.refreshAccessToken();
    if (next) {
      response = await run(next);
    }
  }
  return response;
}

/** Lazy-instantiated default client. Use this from feature services. */
let defaultClient: ReturnType<typeof createApiClient> | null = null;
export function api(): ReturnType<typeof createApiClient> {
  if (defaultClient === null) {
    defaultClient = createApiClient();
  }
  return defaultClient;
}

/** Test-only: reset the cached default client. */
export function __resetApiClientForTests(): void {
  defaultClient = null;
  defaultAuthHandlers = null;
}

export { ApiError, ApiNetworkError };
